/**
 * «Tu cuenta se eliminará el…», al entrar durante la ventana de gracia.
 *
 * Sale en lugar del panel cuando la sesión trae la marca del worker
 * (`deletion_purge_at`). Restaurar es un botón y no algo que pase sólo con
 * entrar: quien entra «a ver» no puede deshacer sin querer algo que decidió.
 *
 * Antes de pulsar dice lo que NO vuelve solo, para que nadie se lleve una
 * sorpresa: los sitios hay que publicarlos otra vez, los dominios conectarlos y
 * la membresía quedó cancelada.
 */
import { useState } from 'react'
import { IconLoader2 } from '@tabler/icons-react'
import type { Session } from '@supabase/supabase-js'
import { AuthLayout } from './Login'
import { useI18n, translateServerError } from '../lib/i18n'
import { restoreAccount } from '../lib/api'
import { supabase } from '../lib/supabase'
import { signOutEverywhere } from '../lib/editorSession'
import { clearScheduledDeletion } from '../lib/accountDeletionNotice'
import type { VelticaTheme } from '@webbuilder/ui'

export function AccountRestore({ session, purgeAt, theme, onToggleTheme }: {
  session: Session
  purgeAt: string
  theme: VelticaTheme
  onToggleTheme: () => void
}) {
  const { t, fmt } = useI18n()
  const [busy, setBusy] = useState<'restore' | 'signout' | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function restore() {
    setBusy('restore')
    setError(null)
    try {
      await restoreAccount(session.access_token)
      clearScheduledDeletion()
      /* La sesión trae la marca vieja: se renueva para que el panel ya no la vea. */
      await supabase.auth.refreshSession()
      window.location.replace('/')
    } catch (e) {
      setError(translateServerError(e, t('account.restore.failed')))
      setBusy(null)
    }
  }

  return (
    <AuthLayout variant="login" theme={theme} onToggleTheme={onToggleTheme}>
      <div className="login-copy">
        <h1>{t('account.restore.title', { date: fmt.date(purgeAt) })}</h1>
        <p>{t('account.restore.lead')}</p>
      </div>

      <ul className="login-restore-list">
        <li>{t('account.restore.sites')}</li>
        <li>{t('account.restore.domains')}</li>
        <li>{t('account.restore.plan')}</li>
      </ul>

      {error ? <p className="login-alert login-alert--error">{error}</p> : null}

      <button className="login-primary-btn" type="button" disabled={busy !== null} onClick={() => void restore()}>
        {busy === 'restore' ? <IconLoader2 className="dash-spin" size={16} /> : null}
        {t(busy === 'restore' ? 'account.restore.restoring' : 'account.restore.cta')}
      </button>

      <p className="login-auth-switch">
        {t('account.restore.keepDeleting')}{' '}
        <button
          type="button"
          className="login-text-btn"
          disabled={busy !== null}
          onClick={() => { setBusy('signout'); void signOutEverywhere() }}
        >
          {t('account.restore.signOut')}
        </button>
      </p>
    </AuthLayout>
  )
}
